import { BankOperationRelatedTypes } from 'constants/bankOperationTypes';

import { useEffect } from 'react';

import { required } from 'react-admin';
import { useFormContext } from 'react-hook-form';
import { Box } from 'shared/mui/Box';
import { TextField } from 'shared/mui/TextField/TextField';
import { Typography } from 'shared/mui/Typography';
import { AutocompleteInput, TextInput } from 'shared/react-admin/Inputs';
import { ReferenceInput } from 'shared/react-admin/ReferenceInputs';
import { fieldWidth } from 'themes/layout';
import { Currency } from 'types/embedded/currency';
import { getId } from 'utils';
import { cleanEmpty } from 'utils/cleanEmpty';

import { BoxStyled } from './RequestsBankOpsForm.styled';

export const RequestsBankOpsFormRelated: React.FC = () => {
  const { setValue, watch, clearErrors, setError } = useFormContext();
  const [
    referral,
    merchant,
    relatedProvider,
    relatedMerchantAccount,
    type,
    provider,
    merchantAccount,
  ] = watch([
    'related.referral',
    'related.merchant',
    'related.provider',
    'related.merchantAccount',
    'type',
    'provider',
    'merchantAccount',
  ]);

  useEffect(() => {
    if (
      getId(type) === BankOperationRelatedTypes.ConversionReferrals &&
      !!relatedProvider &&
      relatedProvider !== provider
    ) {
      setError('provider', { type: 'manual', message: 'Должны быть идентичны' });
      setError('related.provider', { type: 'manual', message: 'Должны быть идентичны' });
    } else {
      clearErrors(['provider', 'related.provider']);
    }
  }, [relatedProvider, provider, type]);

  useEffect(() => {
    if (
      getId(type) === BankOperationRelatedTypes.Transfer &&
      !!relatedProvider &&
      relatedProvider === provider
    ) {
      setError('provider', { type: 'manual', message: 'Не должны быть идентичны' });
      setError('related.provider', { type: 'manual', message: 'Не должны быть идентичны' });
    } else {
      clearErrors(['merchantAccount', 'related.merchantAccount']);
    }
  }, [relatedProvider, provider, type]);

  useEffect(() => {
    if (!!relatedMerchantAccount && relatedMerchantAccount === merchantAccount) {
      setError('merchantAccount', { type: 'manual', message: 'Не должны быть идентичны' });
      setError('related.merchantAccount', { type: 'manual', message: 'Не должны быть идентичны' });
    } else {
      clearErrors(['merchantAccount', 'related.merchantAccount']);
    }
  }, [relatedMerchantAccount, merchantAccount, type]);

  return (
    <BoxStyled>
      <ReferenceInput perPage={200} reference="referrals" source="related.referral">
        <AutocompleteInput
          filterToQuery={(searchText: string) => ({ name: searchText })}
          fullWidth
          label="Реферал"
          onChange={() => {
            setValue('related.merchant', null);
            setValue('related.provider', null);
            setValue('related.merchantAccount', null);
          }}
          optionText="name"
          readOnly
          sx={fieldWidth.auto}
          validate={required()}
          variant="outlined"
        />
      </ReferenceInput>
      <ReferenceInput
        filter={cleanEmpty({ referrals: referral })}
        reference="merchants"
        source="related.merchant"
      >
        <AutocompleteInput
          clearOnBlur={false}
          filterToQuery={(searchText: string) => ({ name: searchText })}
          fullWidth
          label="Мерчант"
          onChange={() => {
            setValue('related.provider', null);
            setValue('related.merchantAccount', null);
          }}
          optionText="name"
          readOnly
          sx={fieldWidth.auto}
          validate={required()}
          variant="outlined"
        />
      </ReferenceInput>
      <ReferenceInput
        filter={{ merchantAccounts: { merchants: merchant } }}
        perPage={200}
        reference="providers"
        source="related.provider"
      >
        <AutocompleteInput
          disabled={!merchant}
          filterToQuery={(searchText: string) => ({ name: searchText })}
          fullWidth
          label="Провайдер"
          onChange={() => {
            setValue('related.merchantAccount', null);
          }}
          optionText="name"
          readOnly={getId(type) === BankOperationRelatedTypes.Conversion}
          sx={{ ...fieldWidth.auto, gridColumn: ' 3 / span 2' }}
          validate={required()}
          variant="outlined"
        />
      </ReferenceInput>
      <ReferenceInput reference="directions" source="related.direction">
        <AutocompleteInput
          clearOnBlur={false}
          filterToQuery={(searchText: string) => ({ name: searchText })}
          fullWidth
          label="Направлениe"
          optionText="description"
          readOnly
          sx={{ opacity: 0.5 }}
          validate={required()}
          variant="outlined"
        />
      </ReferenceInput>
      <ReferenceInput
        disableReferenceFilter
        enableGetChoices={() => !!relatedProvider}
        filter={{ merchants: merchant, provider: relatedProvider, partner: false }}
        reference="merchant_accounts"
        source="related.merchantAccount"
      >
        <AutocompleteInput
          clearOnBlur={false}
          disabled={!relatedProvider}
          filterToQuery={(searchText: string) => ({ name: searchText })}
          fullWidth
          label="Счет мерчанта"
          onChange={(_, record: any) => {
            setValue('related.currency', (record?.currency as Currency)?.['@id']);
            clearErrors('merchantAccount');
          }}
          optionText="name"
          sx={fieldWidth.auto}
          validate={required()}
          variant="outlined"
        />
      </ReferenceInput>
      {!relatedMerchantAccount ? (
        <Box mt="4px" sx={{ ...fieldWidth.auto, textAlign: 'center' }} width="100%">
          <Typography color="text.secondary" fontSize={12} lineHeight="16px">
            Значение валюты автоматически принимает значение выбранного счета
          </Typography>
        </Box>
      ) : (
        <ReferenceInput name="related.currency" reference="currencies" source="related.currency">
          <AutocompleteInput
            fullWidth
            isRequired
            optionText={(record: Currency) => `${record.alphaCode}, ${record.name}`}
            readOnly
            renderInput={(params) => (
              <TextField
                {...params}
                InputProps={{ endAdornment: false }}
                size="small"
                sx={{ backgroundColor: 'rgba(250, 250, 250, 1)' }}
                variant="outlined"
              />
            )}
            variant="outlined"
          />
        </ReferenceInput>
      )}
      <TextInput
        fullWidth
        label="Сумма"
        parse={(v) => v.replace(',', '.')}
        source="related.amountPenny"
        validate={[required()]}
        variant="outlined"
      />
    </BoxStyled>
  );
};
